/** Method-scoped internal mobile safety recordings. Shared by API billing
 * gates and native controls; feature, role and operational gates stay separate.
 * This catalogue shares routes and methods, not native presentation states.
 * Server safety exemptions deliberately apply whatever the billing state.
 * Unknown actions fail closed. Record deletion and AI stay restricted.
 * Revoking a crew signing credential remains available for containment.
 * Worker portal authority is deliberately separate. */
const DELETE_ONLY: ReadonlySet<string> = new Set(["DELETE"]);
const POST_ONLY: ReadonlySet<string> = new Set(["POST"]);
const PATCH_ONLY: ReadonlySet<string> = new Set(["PATCH"]);
export const MOBILE_SAFETY_WRITES = {
  evacuationStart: {
    pattern: /^\/api\/mobile\/v1\/projects\/[^/]+\/access\/evacuations$/,
    methods: POST_ONLY,
    example: "/api/mobile/v1/projects/p_example/access/evacuations",
    reason:
      "SAFETY: starting an evacuation from the app opens the emergency roll-call — a life-safety write never conditioned on an invoice (web twin: POST /api/projects/:id/evacuations).",
  },
  evacuationEnd: {
    pattern: /^\/api\/mobile\/v1\/projects\/[^/]+\/access\/evacuations\/[^/]+$/,
    methods: PATCH_ONLY,
    example: "/api/mobile/v1/projects/p_example/access/evacuations/e_example",
    reason:
      "SAFETY: ending (standing down) an evacuation from the app closes the emergency roll-call record (web twin: PATCH /api/projects/:id/evacuations/:eventId).",
  },
  evacuationMarkSafe: {
    pattern: /^\/api\/mobile\/v1\/projects\/[^/]+\/access\/evacuations\/[^/]+\/entries\/[^/]+$/,
    methods: PATCH_ONLY,
    example: "/api/mobile/v1/projects/p_example/access/evacuations/e_example/entries/en_example",
    reason:
      "SAFETY: marking a person safe on the roll-call from the app is the emergency record itself (web twin: PATCH /api/projects/:id/evacuations/:eventId/entries/:entryId).",
  },
  incidentCreate: {
    pattern: /^\/api\/mobile\/v1\/projects\/[^/]+\/incidents$/,
    methods: POST_ONLY,
    example: "/api/mobile/v1/projects/p_example/incidents",
    reason:
      "SAFETY / STATUTORY: recording an incident from the app — the incident log is a statutory record an employer must keep whatever its billing state (web twin: POST /api/projects/:id/incidents).",
  },
  incidentUpdate: {
    pattern: /^\/api\/mobile\/v1\/projects\/[^/]+\/incidents\/[^/]+$/,
    methods: PATCH_ONLY,
    example: "/api/mobile/v1/projects/p_example/incidents/i_example",
    reason:
      "SAFETY / STATUTORY: updating an incident record from the app keeps the statutory incident log accurate. DELETE (irreversible removal) stays refused (web twin: PATCH /api/projects/:id/incidents/:incidentId).",
  },
  attendanceUpdate: {
    pattern: /^\/api\/mobile\/v1\/projects\/[^/]+\/access\/attendance\/[^/]+$/,
    methods: PATCH_ONLY,
    example: "/api/mobile/v1/projects/p_example/access/attendance/s_example",
    reason:
      "SAFETY: checking a person out / correcting their attendance from the app keeps the roll-call source true (web twin: POST /api/projects/:id/attendance and PATCH .../attendance/:attendanceId).",
  },
  joinRequestReview: {
    pattern: /^\/api\/mobile\/v1\/projects\/[^/]+\/access\/join-requests\/[^/]+$/,
    methods: PATCH_ONLY,
    example: "/api/mobile/v1/projects/p_example/access/join-requests/r_example",
    reason:
      "SAFETY: a manager's manual approve / reject of a site join request from the app decides who is inducted and on the roll-call; approval passes SITE_SAFETY_BILLING_INTENT to the capacity lock (web twin: PATCH /api/projects/:id/site-join-requests/:requestId).",
  },
  inspectionCreate: {
    pattern: /^\/api\/mobile\/v1\/projects\/[^/]+\/inspections$/,
    methods: POST_ONLY,
    example: "/api/mobile/v1/projects/p_example/inspections",
    reason:
      "SAFETY: recording a site inspection from the app must stay possible whatever the billing state (web twin: POST /api/projects/:id/inspections).",
  },
  inspectionUpdate: {
    pattern: /^\/api\/mobile\/v1\/projects\/[^/]+\/inspections\/[^/]+$/,
    methods: PATCH_ONLY,
    example: "/api/mobile/v1/projects/p_example/inspections/i_example",
    reason:
      "SAFETY: completing or correcting an inspection from the app keeps the site's inspection record accurate. DELETE stays refused (web twin: PATCH /api/projects/:id/inspections/:inspectionId).",
  },
  inspectionActionCreate: {
    pattern: /^\/api\/mobile\/v1\/projects\/[^/]+\/inspections\/[^/]+\/actions$/,
    methods: POST_ONLY,
    example: "/api/mobile/v1/projects/p_example/inspections/i_example/actions",
    reason:
      "SAFETY: raising a corrective action from an inspection finding in the app is part of recording the inspection (web twin: POST /api/projects/:id/inspections/:inspectionId/actions).",
  },
  inspectionActionUpdate: {
    pattern: /^\/api\/mobile\/v1\/projects\/[^/]+\/inspections\/[^/]+\/actions\/[^/]+$/,
    methods: PATCH_ONLY,
    example: "/api/mobile/v1/projects/p_example/inspections/i_example/actions/x_example",
    reason:
      "SAFETY: closing or updating an inspection's corrective action in the app records that the defect was dealt with. DELETE stays refused (web twin: PATCH /api/projects/:id/inspections/:inspectionId/actions/:actionId).",
  },
  documentShare: {
    pattern: /^\/api\/mobile\/v1\/documents\/[^/]+\/share$/,
    methods: POST_ONLY,
    example: "/api/mobile/v1/documents/doc_example/share",
    reason:
      "SAFETY: issuing a document's sign / share link from the app is how workers keep signing; the publication passes SITE_SAFETY_BILLING_INTENT to its locked re-check (web twin: POST /api/documents/:id/share).",
  },
  documentConfirmIssue: {
    pattern: /^\/api\/mobile\/v1\/documents\/[^/]+\/versions\/[^/]+\/confirm-issue$/,
    methods: POST_ONLY,
    example: "/api/mobile/v1/documents/doc_example/versions/v_example/confirm-issue",
    reason:
      "SAFETY: acknowledging a version as issued from the app is the prerequisite the share route checks before issuing its sign / share link (web twin: POST /api/documents/:id/versions/:versionId/confirm-issue).",
  },
  crewLinkIssue: {
    pattern: /^\/api\/mobile\/v1\/projects\/[^/]+\/crew-link$/,
    methods: POST_ONLY,
    example: "/api/mobile/v1/projects/p_example/crew-link",
    reason:
      "SAFETY: issuing the crew sign-off link from the app — the all-plans link workers sign their RAMS and inductions through (web twin: POST /api/projects/:id/crew-link).",
  },
  crewLinkRevoke: {
    pattern: /^\/api\/mobile\/v1\/projects\/[^/]+\/crew-link$/,
    methods: DELETE_ONLY,
    example: "/api/mobile/v1/projects/p_example/crew-link",
    reason:
      "REVOKE: revoking the project's crew sign-off links from the app cuts a possibly leaked public URL (web twin: DELETE /api/projects/:id/crew-link).",
  },
} as const;
export type MobileSafetyAction = keyof typeof MOBILE_SAFETY_WRITES;
